/**
 * soldierTemplates.ts
 *
 * Official soldier templates from Frostgrave 2e.
 * Stats and costs are fixed and derived from official sources.
 * Users cannot modify stats/cost; only metadata (name/notes/tags) is mutable.
 */

import type { SoldierTemplate, SoldierTemplateId } from './types';

/**
 * Complete list of official soldier templates.
 * Data sourced from Frostgrave 2e public resources.
 *
 * Stats explanation:
 * - Move: inches per action
 * - Fight: modifier to melee combat rolls
 * - Shoot: modifier to ranged combat rolls
 * - Armor: target number to hit this figure
 * - Will: modifier to Will rolls
 * - Health: hit points
 * - Cost: gold crowns to hire
 *
 * Specialist flag:
 * - false = Standard soldier (Thug, Thief) - no cap
 * - true = Specialist - counts toward 4-specialist limit
 */
export const SOLDIER_TEMPLATES: Record<SoldierTemplateId, SoldierTemplate> = {
  // ---------------------------------------------------------------------------
  // Standard Soldiers (not specialists)
  // ---------------------------------------------------------------------------
  thug: {
    id: 'thug',
    name: 'Thug',
    move: 6,
    fight: 2,
    shoot: 0,
    armor: 10,
    will: -1,
    health: 10,
    cost: 0,
    isSpecialist: false,
  },
  thief: {
    id: 'thief',
    name: 'Thief',
    move: 7,
    fight: 1,
    shoot: 0,
    armor: 10,
    will: 0,
    health: 10,
    cost: 0,
    isSpecialist: false,
  },

  // ---------------------------------------------------------------------------
  // Specialists (counts toward 4-specialist limit)
  // ---------------------------------------------------------------------------
  'war-hound': {
    id: 'war-hound',
    name: 'War Hound',
    move: 8,
    fight: 1,
    shoot: 0,
    armor: 10,
    will: -2,
    health: 8,
    cost: 10,
    isSpecialist: true,
  },
  infantryman: {
    id: 'infantryman',
    name: 'Infantryman',
    move: 6,
    fight: 3,
    shoot: 0,
    armor: 11,
    will: 0,
    health: 10,
    cost: 50,
    isSpecialist: true,
  },
  'man-at-arms': {
    id: 'man-at-arms',
    name: 'Man-at-arms',
    move: 6,
    fight: 3,
    shoot: 0,
    armor: 12,
    will: 1,
    health: 12,
    cost: 75,
    isSpecialist: true,
  },
  apothecary: {
    id: 'apothecary',
    name: 'Apothecary',
    move: 6,
    fight: 1,
    shoot: 0,
    armor: 10,
    will: 3,
    health: 12,
    cost: 75,
    isSpecialist: true,
  },
  archer: {
    id: 'archer',
    name: 'Archer',
    move: 6,
    fight: 1,
    shoot: 2,
    armor: 11,
    will: 0,
    health: 10,
    cost: 75,
    isSpecialist: true,
  },
  crossbowman: {
    id: 'crossbowman',
    name: 'Crossbowman',
    move: 6,
    fight: 1,
    shoot: 2,
    armor: 11,
    will: 0,
    health: 10,
    cost: 75,
    isSpecialist: true,
  },
  'treasure-hunter': {
    id: 'treasure-hunter',
    name: 'Treasure Hunter',
    move: 7,
    fight: 3,
    shoot: 0,
    armor: 11,
    will: 2,
    health: 12,
    cost: 100,
    isSpecialist: true,
  },
  tracker: {
    id: 'tracker',
    name: 'Tracker',
    move: 7,
    fight: 1,
    shoot: 2,
    armor: 11,
    will: 1,
    health: 12,
    cost: 100,
    isSpecialist: true,
  },
  knight: {
    id: 'knight',
    name: 'Knight',
    move: 5,
    fight: 4,
    shoot: 0,
    armor: 13,
    will: 1,
    health: 12,
    cost: 125,
    isSpecialist: true,
  },
  templar: {
    id: 'templar',
    name: 'Templar',
    move: 5,
    fight: 4,
    shoot: 0,
    armor: 12,
    will: 1,
    health: 12,
    cost: 125,
    isSpecialist: true,
  },
  ranger: {
    id: 'ranger',
    name: 'Ranger',
    move: 7,
    fight: 2,
    shoot: 2,
    armor: 11,
    will: 2,
    health: 12,
    cost: 125,
    isSpecialist: true,
  },
  barbarian: {
    id: 'barbarian',
    name: 'Barbarian',
    move: 6,
    fight: 4,
    shoot: 0,
    armor: 10,
    will: 3,
    health: 14,
    cost: 125,
    isSpecialist: true,
  },
  marksman: {
    id: 'marksman',
    name: 'Marksman',
    move: 5,
    fight: 2,
    shoot: 2,
    armor: 12,
    will: 1,
    health: 12,
    cost: 125,
    isSpecialist: true,
  },
} as const;

/**
 * Get a soldier template by ID.
 * @param templateId - The template ID to look up
 * @returns The template or undefined if not found
 */
export function getSoldierTemplate(
  templateId: SoldierTemplateId
): SoldierTemplate | undefined {
  return SOLDIER_TEMPLATES[templateId];
}

/**
 * Check if a template ID is valid.
 * @param templateId - The template ID to validate
 * @returns true if the template exists
 */
export function isValidTemplateId(
  templateId: string
): templateId is SoldierTemplateId {
  return templateId in SOLDIER_TEMPLATES;
}

/**
 * Get all available template IDs.
 * @returns Array of all soldier template IDs
 */
export function getAllTemplateIds(): SoldierTemplateId[] {
  return Object.keys(SOLDIER_TEMPLATES) as SoldierTemplateId[];
}

/**
 * Get all specialist template IDs.
 * @returns Array of specialist template IDs
 */
export function getSpecialistTemplateIds(): SoldierTemplateId[] {
  return getAllTemplateIds().filter((id) => SOLDIER_TEMPLATES[id].isSpecialist);
}

/**
 * Get all standard (non-specialist) template IDs.
 * @returns Array of standard template IDs (Thug, Thief)
 */
export function getStandardTemplateIds(): SoldierTemplateId[] {
  return getAllTemplateIds().filter((id) => !SOLDIER_TEMPLATES[id].isSpecialist);
}

/**
 * Calculate the total cost for hiring a soldier.
 * @param templateId - The template to get cost for
 * @returns The cost in gold crowns, or 0 if template not found
 */
export function getSoldierCost(templateId: SoldierTemplateId): number {
  return SOLDIER_TEMPLATES[templateId]?.cost ?? 0;
}

